import { CancelToken } from 'axios';
import { dailySessionNotes } from '../constants';
import useAxiosInterceptor from '../../interceptor';

class DailySessionNotesService {
  private axiosInstance;

  constructor() {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- factory, not a React hook (matches npcLogService)
    this.axiosInstance = useAxiosInterceptor();
  }

  getPatientList = (payload: any, cancelToken?: CancelToken) => {
    return this.axiosInstance.get(`${dailySessionNotes?.v2All}`, {
      params: {
        ...payload,
        searchString: payload?.searchString,
      },
      cancelToken,
    });
  };
  createSoapNote = (payload: any) => {
    return this.axiosInstance.put(
      `${dailySessionNotes?.createNotes}/${payload?.patientId}`,
      {},
      {
        params: payload?.params,
      }
    );
  };
  getPatientInfo = (payload: any) => {
    return this.axiosInstance.get(
      `${dailySessionNotes?.index}/${payload?.patientId}/therapy-info`,
      {
        params: payload,
      }
    );
  };
  getOtherPatientInfo = (payload: any, cancelToken?: CancelToken) => {
    return this.axiosInstance.get(`${dailySessionNotes?.otherPatient}`, {
      params: payload,
      cancelToken,
    });
  };
  getDailySessionNotes = (payload: any, cancelToken?: CancelToken) => {
    const patientId = payload?.patientId;
    delete payload?.patientId;
    return this.axiosInstance.get(`${dailySessionNotes?.index}/${patientId}`, {
      params: payload,
      cancelToken,
    });
  };

  checkDailyNotSign = (payload: any) => {
    return this.axiosInstance.put(`${dailySessionNotes?.checkESign}`, null, {
      params: payload,
    });
  };
  updateDailySessionNotes = (payload: any) => {
    return this.axiosInstance.put(
      `${dailySessionNotes?.index}/${payload?.patientId}/notes-id/${payload?.notesId}`,
      payload?.body,
      {
        params: payload?.params,
      }
    );
  };

  getPatientNotesTemplate = (uuid: string) => {
    return this.axiosInstance.get(`${dailySessionNotes.getTemplate}/${uuid}`);
  };

  submitESign = (payload: object) => {
    return this.axiosInstance.put(dailySessionNotes.submitEsign, null, {
      params: { ...payload },
    });
  };

  getTherapyInfoV2 = (payload: any) => {
    return this.axiosInstance.get(
      `${dailySessionNotes?.indexV2}/${payload?.patientId}/therapy-info`,
      {
        params: payload,
      }
    );
  };

  getPrintStatus = (payload: any) => {
    return this.axiosInstance.get(`${dailySessionNotes?.printStatus}`, {
      params: payload,
    });
  };

  // DELETE /provider/v2/daily-notes/{dsnUuid} with a required JSON body { reason }.
  // dsnUuid is the DSN's dailySessionNoteUuid (not the form-submission uuid).
  deleteDailySessionNote = (payload: any) => {
    return this.axiosInstance.delete(`${dailySessionNotes?.indexV2}/${payload?.dsnUuid}`, {
      data: { reason: payload?.reason },
    });
  };

  holdFromBilling = (payload: any) => {
    return this.axiosInstance.put(`${dailySessionNotes?.holdFromBilling}`, [
      { uuid: payload?.dailySessionNoteUuid, hold: payload?.hold },
    ]);
  };

  //  BOPS-151: archive a daily note. POST /provider/v2/daily-notes/{dsnUuid}/archive with a
  //  required { reason }. dsnUuid is the DSN's dailySessionNoteUuid, the same id the delete
  //  takes — archiving runs that delete underneath.
  archiveDailySessionNote = (payload: any) => {
    return this.axiosInstance.post(dailySessionNotes?.archive(payload?.dsnUuid), {
      reason: payload?.reason,
    });
  };

  //  BOPS-151: the archived notes for one episode of care — the source for the Archived
  //  Documents section.
  getArchivedDocuments = (payload: any) => {
    return this.axiosInstance.get(
      dailySessionNotes?.archivedDocumentsByEpisode(payload?.episodeOfCareId)
    );
  };
}

const dailySessionNotesService = new DailySessionNotesService();

Object.freeze(dailySessionNotesService);

export default dailySessionNotesService;
